import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { AppleCategoriesDropdown, AppleCategoriesDropdownOption } from './AppleCategoriesDropdown';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';
import { APPLE_PODCAST_CATEGORIES } from '@insights_module/constants/contextualAndEntity';

function getOptions(tierData: object): AppleCategoriesDropdownOption[] {
  return Object.keys(tierData).map((key) => {
    const subTier = tierData[key as keyof typeof tierData];
    return {
      value: key,
      label: <Option>{key}</Option>,
      children: getOptions(subTier),
    };
  });
}

function findOptionByValue(
  options: AppleCategoriesDropdownOption[],
  value: string
): AppleCategoriesDropdownOption | null {
  for (const option of options) {
    if (option.value === value) {
      return option;
    }
    if (option.children) {
      const foundOption = findOptionByValue(option.children, value);
      if (foundOption) {
        return foundOption;
      }
    }
  }
  return null;
}

export const SegmentCreatorAppleCategoriesItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { value, condition } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    { value: 'includes', label: <Option>{t('insights.query_builder.conditions.is')}</Option> },
    { value: 'excludes', label: <Option>{t('insights.query_builder.conditions.is_not')}</Option> },
  ];
  const categoriesOptions = getOptions(APPLE_PODCAST_CATEGORIES);

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find((item) => item.value === (condition as unknown as string)) ||
      conditionOptions[0]
  );
  const [selectedAppleCategory, setSelectedAppleCategory] =
    useState<AppleCategoriesDropdownOption | null>(
      findOptionByValue(categoriesOptions, value as string)
    );
  const [categoryAutoFocus, setCategoryAutoFocus] = useState<boolean>(false);

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedAppleCategory?.value && selectedCondition) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as AQLQueryCondition,
        value: selectedAppleCategory.value,
      };
      pushSegmentData(segment);
    }
  }, [selectedAppleCategory?.value, selectedCondition]);

  useEffect(() => {
    if (!segmentData.value) {
      setCategoryAutoFocus(true);
    }
  }, []);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-apple-filter-bg border border-spec-apple-filter-stroke text-spec-apple-filter-text font-semibold`}
        caretStyle={tw`fill-spec-apple-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        options={conditionOptions}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <AppleCategoriesDropdown
        autoFocus={categoryAutoFocus}
        options={categoriesOptions}
        value={selectedAppleCategory}
        onChange={(val) => setSelectedAppleCategory(val as AppleCategoriesDropdownOption)}
        searchPlaceholder={t('insights.query_builder.apple.search_placeholder')}
        isPresentational={isPresentational}
      />
    </div>
  );
};
